/* Copyright Flexday Solutions LLC, Inc - All Rights Reserved

 * Unauthorized copying of this file, via any medium is strictly prohibited

 * Proprietary and confidential

 * See file LICENSE.txt for full license details.

 */

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { object, string, array } from 'yup';
import { Form } from 'formik';
import { Chip } from '@mui/material';

import PageContainer from '../../../components/pageContainer';
import { SECTIONS } from '../../../constants/drawer';
import AddPageHeader from '../../../components/form/addPageHeader.component';
import { ACCESS_CONTROL } from '../../../constants/path';
import { USERS } from '../../../constants/accessControlPaths';
import AppForm, {
  AppAutocompleteField,
  AppTextField,
  FormButtons,
} from '../../../components/form';
import { getChangedValues } from '../../../components/form/functions';
import { FormContainer, InputsContainer } from '../styled';
import {
  RBAC_USERS_ENDPOINT,
  RBAC_GROUPS_ENDPOINT,
  RBAC_ROLES_ENDPOINT,
  RBAC_POLICIES_ENDPOINT,
} from '../../../constants/accessControl';
import { AlertStyled } from '../../settings/filesCollectionSettingsPage/styled';
import {
  useCreateRBACMutation,
  useLazyGetAllRBACQuery,
  useLazyGetOneRBACQuery,
  useUpdateRBACMutation,
} from '../../../redux/services/speciphicAsk';
import Loader from '../../../components/loader/loader.component';
import { filterByIds } from '../../../utils/functions';

const mainPageRoute = `${ACCESS_CONTROL}${USERS.endpoint}`;

const translationJSONPrefix = 'accessControlSection.users';

const defaultDataState = {
  // sub: '', // to be added later
  emailId: '',
  groups: [],
  roles: [],
  policies: [],
};

const formSchema = (t) => {
  const translationPrefix = (label) =>
    `${translationJSONPrefix}.form.fields.${label}.errorMessages`;

  return object({
    // sub: string(), // to be added later
    emailId: string()
      .required(t(`${translationPrefix('emailId')}.required`))
      .email(t(`${translationPrefix('emailId')}.email`)),
    groups: array().required(t(`${translationPrefix('groups')}.required`)),
    roles: array().required(t(`${translationPrefix('roles')}.required`)),
    policies: array().required(t(`${translationPrefix('policies')}.required`)),
  });
};

const formRenderFunction = (
  { values, errors, touched, dirty, isSubmitting, resetForm, setFieldValue },
  formSending,
  groupsData,
  isGroupsLoading,
  rolesData,
  isRolesLoading,
  policiesData,
  isPoliciesLoading,
) => {
  const { t } = useTranslation();

  const translationPrefix = `${translationJSONPrefix}.form.fields`;

  return (
    <Form>
      {/* /// */}
      {/* to be added later */}
      {/* /// */}
      {/* <InputsContainer>
        <AppTextField
          name="sub"
          label={t(`${translationPrefix}.sub.label`)}
          placeholder={t(`${translationPrefix}.sub.placeholder`)}
          values={values}
          setFieldValue={setFieldValue}
          errors={errors}
          touched={touched}
        />
      </InputsContainer> */}

      <InputsContainer>
        <AppTextField
          name="emailId"
          label={t(`${translationPrefix}.emailId.label`)}
          placeholder={t(`${translationPrefix}.emailId.placeholder`)}
          values={values}
          setFieldValue={setFieldValue}
          errors={errors}
          touched={touched}
        />
      </InputsContainer>

      <InputsContainer>
        <AppAutocompleteField
          name="groups"
          label={t(`${translationPrefix}.groups.label`)}
          placeholder={
            isGroupsLoading
              ? t(`${translationPrefix}.groups.loading`)
              : t(`${translationPrefix}.groups.placeholder`)
          }
          values={values}
          setFieldValue={setFieldValue}
          errors={errors}
          touched={touched}
          options={groupsData}
          getOptionLabel={(option) =>
            `${option?.name} (Role - ${
              rolesData?.filter((r) => r.id === option.roleId)[0]?.name
            }) (${option?.policyCount} group polic${
              option?.policyCount > 1 ? 'ies' : 'y'
            })` || ''
          }
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                key={index}
                label={option?.name || '-'}
                {...getTagProps({ index })}
              />
            ))
          }
        />
      </InputsContainer>

      <InputsContainer>
        <AppAutocompleteField
          name="roles"
          label={t(`${translationPrefix}.roles.label`)}
          placeholder={
            isRolesLoading
              ? t(`${translationPrefix}.roles.loading`)
              : t(`${translationPrefix}.roles.placeholder`)
          }
          values={values}
          setFieldValue={setFieldValue}
          errors={errors}
          touched={touched}
          options={rolesData}
          getOptionLabel={(option) =>
            `${option?.name} (${option?.policyCount} polic${
              option?.policyCount > 1 ? 'ies' : 'y'
            })` || ''
          }
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                key={index}
                label={option?.name || '-'}
                {...getTagProps({ index })}
              />
            ))
          }
        />
      </InputsContainer>

      <InputsContainer>
        <AppAutocompleteField
          name="policies"
          label={t(`${translationPrefix}.policies.label`)}
          placeholder={
            isPoliciesLoading
              ? t(`${translationPrefix}.policies.loading`)
              : t(`${translationPrefix}.policies.placeholder`)
          }
          values={values}
          setFieldValue={setFieldValue}
          errors={errors}
          touched={touched}
          options={policiesData}
          getOptionLabel={(option) =>
            `${option?.name} (${option?.actions?.length} action${
              option?.actions?.length > 1 ? 's' : ''
            })` || ''
          }
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                key={index}
                label={option?.name || '-'}
                {...getTagProps({ index })}
              />
            ))
          }
        />
      </InputsContainer>

      <FormButtons
        resetForm={resetForm}
        dirty={dirty}
        isSubmitting={isSubmitting || formSending}
      />
    </Form>
  );
};

const AccessControlAddUsersGroupPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [initialFormData, setInitialFormData] = useState(defaultDataState);
  const [formData, setFormData] = useState(defaultDataState);
  const [showAlert, setShowAlert] = useState({
    formUpdateError: false,
    formUpdateSuccess: false,
  });
  const [isEdit, setIsEdit] = useState(false);
  const [entityId, setEntityId] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const [
    createRBAC,
    {
      data: createdData,
      error: createError,
      isError: isCreateError,
      isLoading: isCreateLoading,
      isSuccess: isCreateSuccess,
    },
  ] = useCreateRBACMutation();

  const [
    updateRBAC,
    {
      data: updatedData,
      error: updateError,
      isError: isUpdateError,
      isLoading: isUpdateLoading,
      isSuccess: isUpdateSuccess,
    },
  ] = useUpdateRBACMutation();

  const [
    getRBAC,
    {
      data: rbacData,
      isError: isDataError,
      isLoading: isDataLoading,
      isSuccess: isDataSuccess,
    },
  ] = useLazyGetOneRBACQuery();

  const [
    getGroupsRBAC,
    {
      data: groupsData,
      isLoading: isGroupsLoading,
      isSuccess: isGroupsSuccess,
    },
  ] = useLazyGetAllRBACQuery();

  const [
    getRolesRBAC,
    { data: rolesData, isLoading: isRolesLoading, isSuccess: isRolesSuccess },
  ] = useLazyGetAllRBACQuery();

  const [
    getPoliciesRBAC,
    {
      data: policiesData,
      isLoading: isPoliciesLoading,
      isSuccess: isPoliciesSuccess,
    },
  ] = useLazyGetAllRBACQuery();

  useEffect(() => {
    const tempEntityId = searchParams.get('id');

    if (tempEntityId) {
      setIsEdit(true);
      getRBAC({ endpoint: RBAC_USERS_ENDPOINT, id: tempEntityId });
      setEntityId(tempEntityId);
    }

    getGroupsRBAC({ endpoint: RBAC_GROUPS_ENDPOINT });
    getRolesRBAC({ endpoint: RBAC_ROLES_ENDPOINT });
    getPoliciesRBAC({ endpoint: RBAC_POLICIES_ENDPOINT });
  }, []);

  useEffect(() => {
    if (isDataSuccess && rbacData && groupsData && rolesData && policiesData) {
      const tempFormData = {
        emailId: rbacData?.emailId,
        groups: filterByIds(rbacData?.groups, groupsData),
        roles: filterByIds(rbacData?.roles, rolesData),
        policies: filterByIds(rbacData?.policies, policiesData),
      };

      setFormData(tempFormData);
      setInitialFormData(tempFormData);
    }
  }, [rbacData, groupsData, rolesData, policiesData]);

  const closeAlert = () => {
    const tempAlert = { ...showAlert };
    Object.keys(tempAlert).forEach((a) => (tempAlert[a] = false));
    setShowAlert(tempAlert);
  };

  useEffect(() => {
    const tempAlert = { ...showAlert };
    if (isCreateError || isUpdateError) {
      tempAlert.formUpdateError = true;
      tempAlert.formUpdateSuccess = false;
      const tempErrorMessage =
        createError?.data?.message ||
        updateError?.data?.message ||
        t(`${translationJSONPrefix}.form.errorAlertLabel`);
      setErrorMessage(tempErrorMessage);
      setShowAlert(tempAlert);
    }
  }, [isCreateError, isUpdateError]);

  useEffect(() => {
    const tempAlert = { ...showAlert };
    if (isCreateSuccess || isUpdateSuccess) {
      tempAlert.formUpdateSuccess = true;
      tempAlert.formUpdateError = false;
      setShowAlert(tempAlert);
      getRBAC({
        endpoint: RBAC_USERS_ENDPOINT,
        id: createdData?.id || updatedData?.id,
      });
    }
  }, [isCreateSuccess, isUpdateSuccess]);

  useEffect(() => {
    if (showAlert.formUpdateError || showAlert.formUpdateSuccess)
      setTimeout(closeAlert, 5000);
  }, [showAlert.formUpdateError, showAlert.formUpdateSuccess]);

  return (
    <PageContainer drawer={SECTIONS.ACCESS_CONTROL}>
      <AddPageHeader
        routes={[
          {
            title: t(`${translationJSONPrefix}.mainPage.title`),
            endpoint: mainPageRoute,
          },
        ]}
        isEdit={isEdit}
      />

      <FormContainer>
        <AlertStyled
          onClose={closeAlert}
          severity={showAlert.formUpdateError ? 'error' : 'success'}
          style={{
            marginTop:
              showAlert.formUpdateError || showAlert.formUpdateSuccess
                ? 10
                : -60,
          }}
        >
          {showAlert.formUpdateSuccess &&
            (isEdit
              ? t(`${translationJSONPrefix}.form.updatedAlertLabel`)
              : t(`${translationJSONPrefix}.form.createdAlertLabel`))}
          {showAlert.formUpdateError && errorMessage}
        </AlertStyled>

        {isDataLoading ? (
          <Loader label={t(`${translationJSONPrefix}.form.loadingLabel`)} />
        ) : (
          <AppForm
            initialValues={formData}
            validationSchema={formSchema(t)}
            onSubmit={(values, { setSubmitting }) => {
              let valuesToSend = isEdit
                ? getChangedValues(values, initialFormData)
                : { ...values };

              if (valuesToSend?.groups)
                valuesToSend.groups = valuesToSend.groups.map((g) => g.id);
              if (valuesToSend?.roles)
                valuesToSend.roles = valuesToSend.roles.map((r) => r.id);
              if (valuesToSend?.policies)
                valuesToSend.policies = valuesToSend.policies.map((p) => p.id);

              if (isEdit)
                updateRBAC({
                  endpoint: RBAC_USERS_ENDPOINT,
                  id: entityId,
                  update: valuesToSend,
                });
              else
                createRBAC({
                  endpoint: RBAC_USERS_ENDPOINT,
                  body: valuesToSend,
                });

              setSubmitting(false);
            }}
            formRenderFunction={(formikState) =>
              formRenderFunction(
                formikState,
                isCreateLoading || isUpdateLoading,
                groupsData || [],
                isGroupsLoading,
                rolesData || [],
                isRolesLoading,
                policiesData,
                isPoliciesLoading,
              )
            }
          />
        )}
      </FormContainer>
    </PageContainer>
  );
};

export default AccessControlAddUsersGroupPage;
